import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaPlay, FaCheck, FaBell } from 'react-icons/fa';
import {
  fetchAdjustmentHistory,
  simulateRentalAdjustment,
  createRentalAdjustment,
  fetchRentalDetail,
  fetchAdminAdjustmentHistory,
  simulateAdminRentalAdjustment,
  createAdminRentalAdjustment,
  notifyAdminAdjustment,
  fetchAdminRentalDetail,
} from '../../../Redux/Actions/actions';
import {
  RENTAL_TYPE,
  useRentalType,
  formatCurrency,
  formatDate,
  typeLabel,
} from '../utils/rentalTypeHelpers';
import style from './RentalAdjustmentsView.module.css';
import Breadcrumbs from '../../../Components/Breadcrumbs/Breadcrumbs';
import { devLog } from '../../../utils/devLog';

const ADJUSTMENT_TYPES = [
  { value: 'percentage', label: 'Porcentaje' },
  { value: 'fixed', label: 'Monto fijo' },
  { value: 'index', label: 'Índice (IPC)' },
];

const RentalAdjustmentsView = () => {
  const { contractId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const type = useRentalType();
  const isAdmin = type === RENTAL_TYPE.ADMIN;

  const {
    adjustmentHistory,
    adminAdjustmentHistory,
    adminAdjustmentSimulation,
    rentalDetail,
    adminRentalDetail,
    rentalsLoading,
    adminRentalsLoading,
    rentalsError,
    adminRentalsError,
  } = useSelector((s) => s);

  const history = isAdmin ? adminAdjustmentHistory : adjustmentHistory;
  const detail = isAdmin ? adminRentalDetail : rentalDetail;
  const loading = isAdmin ? adminRentalsLoading : rentalsLoading;
  const error = isAdmin ? adminRentalsError : rentalsError;

  const [adjType, setAdjType] = useState('percentage');
  const [value, setValue] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [reason, setReason] = useState('');
  const [simulating, setSimulating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [localSimulation, setLocalSimulation] = useState(null);

  useEffect(() => {
    if (!contractId) return;
    if (isAdmin) {
      dispatch(fetchAdminAdjustmentHistory(contractId));
      if (!adminRentalDetail || String(adminRentalDetail.id) !== String(contractId)) {
        dispatch(fetchAdminRentalDetail(contractId));
      }
    } else {
      dispatch(fetchAdjustmentHistory(contractId));
      if (!rentalDetail || String(rentalDetail.id) !== String(contractId)) {
        dispatch(fetchRentalDetail(contractId));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, contractId, isAdmin]);

  useEffect(() => {
    devLog('RentalAdjustmentsView/data', {
      contractId,
      isAdmin,
      historyCount: history?.length ?? 0,
      hasDetail: !!detail,
      loading,
      error
    });
  }, [history, detail, loading, error, contractId, isAdmin]);

  const handleSimulate = async () => {
    if (!value) {
      toast.error('Ingresá un valor');
      return;
    }
    setSimulating(true);
    try {
      if (isAdmin) {
        await dispatch(
          simulateAdminRentalAdjustment(contractId, {
            type: adjType,
            value: Number(value),
            effectiveDate,
          })
        );
      } else {
        const res = await dispatch(
          simulateRentalAdjustment(contractId, {
            type: adjType,
            value: Number(value),
            effectiveDate,
          })
        );
        setLocalSimulation(res?.data || res);
      }
      toast.success('Simulación calculada');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Error al simular');
    } finally {
      setSimulating(false);
    }
  };

  const handleApply = async () => {
    if (!value) {
      toast.error('Ingresá un valor');
      return;
    }
    if (
      !window.confirm(
        '¿Aplicar este ajuste al contrato? Se actualizará el canon y las cuotas pendientes.'
      )
    )
      return;
    setApplying(true);
    try {
      if (isAdmin) {
        await dispatch(
          createAdminRentalAdjustment(contractId, {
            type: adjType,
            value: Number(value),
            effectiveDate,
            reason: reason.trim() || undefined,
          })
        );
        dispatch(fetchAdminAdjustmentHistory(contractId));
        dispatch(fetchAdminRentalDetail(contractId));
      } else {
        await dispatch(
          createRentalAdjustment(contractId, {
            type: adjType,
            value: Number(value),
            effectiveDate,
            reason: reason.trim() || undefined,
          })
        );
        dispatch(fetchAdjustmentHistory(contractId));
        dispatch(fetchRentalDetail(contractId));
      }
      toast.success('Ajuste aplicado');
      setValue('');
      setReason('');
      setLocalSimulation(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Error al aplicar ajuste');
    } finally {
      setApplying(false);
    }
  };

  const handleNotify = async (adjId) => {
    if (!isAdmin) return;
    try {
      await dispatch(notifyAdminAdjustment(adjId));
      toast.success('Notificación enviada');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Error al notificar');
    }
  };

  const sim = isAdmin ? adminAdjustmentSimulation : localSimulation;

  // Datos comunes
  const rent = isAdmin
    ? detail?.currentRentValue ?? detail?.rentValue
    : detail?.currentRentValue ?? detail?.monthlyRent ?? detail?.rentValue;
  const currency = detail?.currency || 'ARS';
  const adjustmentFrequency = isAdmin
    ? detail?.adjustmentFrequency
    : detail?.adjustmentFrequency || detail?.frecuenciaAjuste;
  const adjustmentTypeCfg = isAdmin
    ? detail?.adjustmentType
    : detail?.adjustmentType || detail?.tipoAjuste;
  const adjustmentValueCfg = isAdmin
    ? detail?.adjustmentValue
    : detail?.adjustmentValue ?? detail?.valorAjuste;
  const nextAdjustmentDate = detail?.nextAdjustmentDate;

  return (
    <div className={style.container}>
      <Breadcrumbs
        items={[
          { label: 'Alquileres', to: '/alquileres' },
          {
            label: `Contrato #${contractId}`,
            to: `/alquileres/contrato/${contractId}${isAdmin ? '?tipo=admin' : ''}`
          },
          { label: 'Ajustes' }
        ]}
      />
      <div className={style.header}>
        <div>
          <button
            type="button"
            className={`${style.button} ${style.secondary}`}
            onClick={() =>
              navigate(
                `/alquileres/contrato/${contractId}${isAdmin ? '?tipo=admin' : ''}`
              )
            }
            style={{ marginBottom: 8 }}
          >
            <FaArrowLeft /> Volver
          </button>
          <h1 className={style.title}>
            Ajustes del contrato
            <span className={`${style.typeBadge} ${isAdmin ? style.admin : ''}`}>
              {typeLabel(type)}
            </span>
          </h1>
          <div className={style.subtitle}>
            Canon actual: <strong>{formatCurrency(rent, currency)}</strong>
          </div>
        </div>
      </div>

      {error && <div className={style.error}>{error}</div>}

      {!isAdmin && (
        <div className={style.notice}>
          <strong>Nota:</strong> Para contratos regulares, los ajustes pueden aplicarse
          según la configuración del contrato. Si no usás ajustes manuales, podés simplemente
          dejar que el sistema los aplique automáticamente al vencer el período configurado.
        </div>
      )}

      <div className={style.grid}>
        <div className={style.card}>
          <h3 className={style.cardTitle}>Nuevo ajuste</h3>
          <div className={style.formGroup}>
            <label>Tipo</label>
            <select
              className={style.select}
              value={adjType}
              onChange={(e) => setAdjType(e.target.value)}
            >
              {ADJUSTMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className={style.formGroup}>
            <label>
              Valor{' '}
              {adjType === 'percentage'
                ? '(%)'
                : adjType === 'fixed'
                ? '(monto nuevo)'
                : '(coeficiente)'}
            </label>
            <input
              className={style.input}
              type="number"
              step="0.01"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={adjType === 'percentage' ? 'Ej: 25' : 'Ej: 350000'}
            />
          </div>
          <div className={style.formGroup}>
            <label>Fecha efectiva</label>
            <input
              className={style.input}
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
            />
          </div>
          <div className={style.formGroup}>
            <label>Motivo (opcional)</label>
            <input
              className={style.input}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ej: Ajuste semestral por contrato"
            />
          </div>
          <div className={style.actions}>
            <button
              type="button"
              className={`${style.button} ${style.secondary}`}
              onClick={handleSimulate}
              disabled={simulating}
            >
              <FaPlay /> {simulating ? 'Simulando...' : 'Simular'}
            </button>
            <button
              type="button"
              className={`${style.button} ${style.primary}`}
              onClick={handleApply}
              disabled={applying}
            >
              <FaCheck /> {applying ? 'Aplicando...' : 'Aplicar'}
            </button>
          </div>

          {sim && (
            <div className={style.simulationResult}>
              <div className={style.row}>
                <span>Canon actual</span>
                <span>{formatCurrency(sim.currentRent, sim.currency || currency)}</span>
              </div>
              <div className={style.row}>
                <span>Variación</span>
                <span>
                  {sim.variationAmount != null
                    ? formatCurrency(sim.variationAmount, sim.currency || currency)
                    : sim.variationPercent != null
                    ? `${sim.variationPercent}%`
                    : '-'}
                </span>
              </div>
              <div className={style.row}>
                <span>Nuevo canon</span>
                <span>{formatCurrency(sim.newRent, sim.currency || currency)}</span>
              </div>
              {sim.affectedPayments != null && (
                <div className={style.row}>
                  <span>Cuotas afectadas</span>
                  <span>{sim.affectedPayments}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className={style.card}>
          <h3 className={style.cardTitle}>Configuración del contrato</h3>
          <div className={style.row}>
            <span>Frecuencia</span>
            <span>{adjustmentFrequency || '-'}</span>
          </div>
          <div className={style.row}>
            <span>Tipo predeterminado</span>
            <span>{adjustmentTypeCfg || '-'}</span>
          </div>
          <div className={style.row}>
            <span>Valor predeterminado</span>
            <span>{adjustmentValueCfg ?? '-'}</span>
          </div>
          {nextAdjustmentDate && (
            <div className={style.row}>
              <span>Próximo ajuste</span>
              <span>{formatDate(nextAdjustmentDate)}</span>
            </div>
          )}
        </div>
      </div>

      <h3 className={style.cardTitle}>Historial de ajustes</h3>
      {loading && !history?.length ? (
        <div className={style.loading}>Cargando historial...</div>
      ) : !history?.length ? (
        <div className={style.empty}>No hay ajustes aplicados todavía.</div>
      ) : (
        history.map((a) => (
          <div key={a.id} className={style.adjustmentItem}>
            <div>
              <div className={style.itemLabel}>
                {a.type} · {a.value}
                {a.type === 'percentage' ? '%' : ''}
              </div>
              <div className={style.itemMeta}>
                {a.reason || 'Sin motivo registrado'}
              </div>
            </div>
            <div>
              <div className={style.itemMeta}>Fecha</div>
              <strong>{formatDate(a.effectiveDate || a.createdAt)}</strong>
            </div>
            <div>
              <div className={style.itemMeta}>Anterior</div>
              <strong>{formatCurrency(a.previousRent, a.currency || currency)}</strong>
            </div>
            <div>
              <div className={style.itemMeta}>Nuevo</div>
              <strong>{formatCurrency(a.newRent, a.currency || currency)}</strong>
            </div>
            <div>
              {isAdmin && (
                <button
                  type="button"
                  className={`${style.button} ${style.warning}`}
                  onClick={() => handleNotify(a.id)}
                  title="Notificar al inquilino"
                >
                  <FaBell />
                </button>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default RentalAdjustmentsView;
